// A page's SECTIONS — the editable copy blocks that make a website page real.
//
// WHAT THIS FINISHES
// Slice 1 made a page a record she owns (see websitePages.js) but left the copy
// on it read-only: one block of locked prose, rendered as text. She could rename
// a page and publish it, but she couldn't change a word on it. These rows are
// that word. A section is hers — reorderable, deletable, and editable — and the
// locked My Website output is where the first ones come FROM, not where they
// keep living. (My Website is a MOMENTUM module, not an Accelerator one — see
// the note in websitePages.js before writing "Accelerator" anywhere here.)
//
// SPLIT ONCE, THEN IT'S HERS
// The first time she opens a page that has locked copy, that copy is split into
// real section rows (`ensureSections`). After that the rows are the truth and the
// locked output is only a comparison point: each seeded row stores the exact text
// the page was split from (`sourceText`) and its hash (`sourceHash`), so if she
// re-locks My Website later the page can say "My Website has been rewritten" and
// offer to re-split. That refresh throws away her edits, so — like the offer
// Resync it mirrors — it is always opt-in and always confirmed first.
//
// WHY SPLITTING IS CONSERVATIVE
// `splitCopy` splits on markdown headings and NOTHING else. Brill's My Website
// output is prose, not a form submission: guessing block boundaries out of
// paragraph shape would chop a page into nonsense on the copy that doesn't
// happen to be laid out the way the guess assumed. Fewer than two headings means
// one block, which is exactly what she had before and therefore never a loss.

import { useCallback, useRef } from "react";
import { reorderWrites } from "./reorder";
import { createSectionStore, pageWords, sectionFields } from "./sectionStore";
import { hashSource } from "./sourceHash";
import { HERO_SECTION, WEBSITE_SOURCE, sourceSectionFor } from "./websitePages";
import { OTHER_KIND, fieldsOf, kindDef } from "./websiteSectionKinds";

// `id`, `createdAt`, `updatedAt` are handled by recordStore — not listed. The
// list itself is `sectionFields("pageId")`: the funnel table has the same
// columns, so the two stores share one definition rather than two that drift.
export const WEBSITE_SECTION_FIELDS = sectionFields("pageId");

/* The nine kinds and their fields live in websiteSectionKinds.js — the one place
   the editor, the parser, the prompt and the word count all read. Re-exported
   here because this is the module the Website panel already imports from, and a
   second import path for the same concept is how two of them drift. */
export {
  SECTION_KINDS, OTHER_KIND, kindDef, kindLabel, kindOf,
  sectionValues, sectionWords, hasContent, wordCount,
} from "./websiteSectionKinds";

/* A SECTION'S STATUS, and why it's only two words.
   `status` has been stored on every section since 0001 and shown nowhere; the
   table's dots stood in for it by asking "does this have copy in it". That can't
   tell written from finished, which is the one thing she wants to see across a
   page. Draft → Done, set by her and by nobody else — no path here promotes a
   section because Brill wrote into it.
   Not "Live": a page is published, a section isn't, and borrowing the page's word
   would say this section is on the internet. */
export const SECTION_DRAFT = "draft";
export const SECTION_DONE = "done";
export const SECTION_STATUS = [[SECTION_DRAFT, "Draft"], [SECTION_DONE, "Done"]];

/** What to call this block on screen: the heading she typed, else the first
 *  heading-shaped field of its kind, else the kind's own name. Never blank —
 *  an unnamed card with an empty body would be a row of nothing. */
export function sectionHeading(section) {
  const content = section?.content || {};
  const kind = kindDef(section?.sectionType);
  for (const f of kind.fields) {
    if (f.key !== "heading" && f.key !== "head") continue;
    const v = String(content[f.key] || "").trim();
    if (v) return v;
  }
  return kind.label;
}

/** The free-form body of an Other block. Typed kinds have no single "text", so
 *  this is empty for them — callers wanting all of a section's words want
 *  `sectionWords`, and callers wanting all of its copy want `sectionValues`. */
export function sectionText(section) {
  return String(section?.content?.text || "");
}

export function isFromMyWebsite(section) {
  return section?.source === WEBSITE_SOURCE;
}

/* Every word on a page — shared with the funnel pages, so it lives in
   sectionStore.js. Re-exported here because this is the module the Website panel
   already imports from. */

/**
 * Copy stored under keys the section's CURRENT kind doesn't render.
 *
 * Switching a block from Other to Banner is deliberately non-destructive — the
 * old `text` stays in the jsonb and comes back if she switches back. But copy
 * that exists and isn't on screen is indistinguishable from copy that was
 * deleted, so the editor says so rather than letting her think she lost it.
 */
export function hiddenFieldKeys(section) {
  const content = section?.content || {};
  const shown = new Set(fieldsOf(section?.sectionType).map((f) => f.key));
  /* A picture is shown wherever the kind has a slot for one. */
  if (kindDef(section?.sectionType).picture) shown.add("pictures");
  return Object.keys(content).filter((k) => {
    if (shown.has(k)) return false;
    const v = content[k];
    if (Array.isArray(v)) return v.some((row) => (typeof row === "object" && row ? Object.values(row).some((x) => String(x || "").trim()) : String(row || "").trim()));
    return !!String(v ?? "").trim();
  });
}

/** The exact locked text a page's blocks are built from. Hashed to tell later
 *  whether she has re-locked My Website since — so it must stay in one place
 *  rather than being rebuilt at each call site. */
export function pageSourceText(page, outputs = {}) {
  const section = sourceSectionFor(page);
  if (!section) return "";
  const body = String(outputs?.[section] || "").trim();
  if (section !== "Homepage") return body;
  const hero = String(outputs?.[HERO_SECTION] || "").trim();
  return [hero, body].filter(Boolean).join("\n\n---\n\n");
}

// A markdown heading line: up to three spaces of indent, 1-6 hashes, then text.
// Trailing hashes (the closed-atx style, `## About ##`) are not part of the label.
const HEADING = /^ {0,3}#{1,6}\s+(.+?)\s*#*$/;

// The heading text as a label: no bold markers, no backticks, no trailing colon,
// and short enough to sit on one row.
function cleanHeading(text) {
  return String(text || "")
    .replace(/[*_`]/g, "")
    .replace(/\s*:\s*$/, "")
    .trim()
    .slice(0, 60);
}

/**
 * Split locked prose into `{ heading, text }` blocks on its markdown headings.
 *
 * Fewer than two headings → one unheaded block, because splitting a page in two
 * because Brill happened to write one `##` is worse than leaving it whole.
 * Anything above the first heading becomes an "Intro" block so no copy is lost —
 * silently dropping a paragraph would be the one truly unacceptable outcome.
 */
export function splitCopy(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];

  const lines = raw.split(/\r?\n/);
  const heads = [];
  lines.forEach((line, i) => { if (HEADING.test(line)) heads.push(i); });
  if (heads.length < 2) return [{ heading: "", text: raw }];

  const blocks = [];
  const intro = lines.slice(0, heads[0]).join("\n").trim();
  if (intro) blocks.push({ heading: "Intro", text: intro });

  heads.forEach((start, n) => {
    const end = n + 1 < heads.length ? heads[n + 1] : lines.length;
    blocks.push({
      heading: cleanHeading(lines[start].match(HEADING)[1]),
      text: lines.slice(start + 1, end).join("\n").trim(),
    });
  });
  return blocks;
}

/**
 * The blocks a page should be split into, as plain data — no ids, no storage.
 * Homepage is the special case it has always been: the locked Hero Statement is
 * its own first block rather than a fifth page or a lost line.
 */
export function sectionBlocks(page, outputs = {}) {
  const section = sourceSectionFor(page);
  if (!section) return [];

  /* Every split block is an OTHER section. Splitting prose at its headings can
     tell where a block starts; it cannot tell whether that block was meant to be
     a Banner or a Services list, and filing her words under fields she never
     wrote them for is worse than an honest box of text. She retypes a block as a
     typed kind when she wants the structure. */
  const blocks = [];
  if (section === "Homepage") {
    const hero = String(outputs?.[HERO_SECTION] || "").trim();
    if (hero) blocks.push({ kind: OTHER_KIND, heading: "Hero", text: hero, sourceSection: HERO_SECTION });
  }
  for (const b of splitCopy(outputs?.[section])) {
    blocks.push({ kind: OTHER_KIND, heading: b.heading, text: b.text, sourceSection: section });
  }
  return blocks;
}

/**
 * Has the locked copy moved since this page was split?
 *
 * Answered from the seeded rows' stored hash, not from a diff of the text —
 * comparing hashes means an edit she made to a block can't read as the source
 * having changed. Returns false when nothing came from My Website, when the
 * locked copy is now empty (a module reset shouldn't nag her to wipe her page),
 * or when the rows predate hashing.
 */
export function sourceChangedFor(page, sections = [], outputs = {}) {
  const current = pageSourceText(page, outputs);
  if (!current) return false;
  const hash = hashSource(current);
  return (sections || []).some((s) => isFromMyWebsite(s) && s.sourceHash && s.sourceHash !== hash);
}

/* The store itself is the shared one — see utils/sectionStore.js. What stays in
   this file is the half only a WEBSITE page has: the split out of locked prose,
   and the resync after she re-locks. */
const useSectionRecords = createSectionStore({
  table: "website_sections",
  parentField: "pageId",
  idPrefix: "wsc",
});

/* Dragging blocks into order is the same problem as dragging modules into
   order, so the renumbering lives in utils/reorder.js and both use it. Still
   exported from here, which is where every caller already looks for it. */
export { reorderWrites };

/**
 * How a page reads at a glance: has she written it, or not?
 *
 * Not live/draft. Nothing in this app reaches the open web, so a publish state
 * describes something that doesn't happen — what she actually wants to see down
 * a list of pages is which ones still need words. A page with blocks that are
 * all empty counts as unwritten, because an empty block is a place to write,
 * not writing.
 *
 * THE MARK MOVED TO THE PAGE, 2026-09-15. Her call, overruling spec R6's "teal
 * when every block is marked Done": "you can change the website rule — it's
 * just my stuff — but I'd like to be able to mark the whole page done, not each
 * section, just to make sure we are doing it that way."
 *
 * That is the better rule and it makes the two panels agree for the first time.
 * A COURSE is marked at the module; a SITE is now marked at the page. L3 —
 * a section, a block — is only written or not. So "is this finished" is asked
 * once per record, at the level she actually thinks in, and there is no longer
 * an L2 state DERIVED from its children that could disagree with them.
 *
 * `website_pages.status` has existed since 0001 and was set by nothing. This is
 * what it is for. No migration: a page that has never been marked has no status
 * and reads as draft, which is exactly what it is.
 *
 * Returns "done" | "draft" | "empty" — the same three words, in the same order,
 * that `moduleStatus` returns, so a dot means one thing across both panels.
 */
export function pageState(page, blocks = []) {
  if (page?.status === SECTION_DONE) return "done";
  return pageWords(blocks) > 0 ? "draft" : "empty";
}

/**
 * Undoing an editing session on one section.
 *
 * WHY THIS IS A WRITE AND NOT A setState.
 * A section's fields autosave on the way out of each one (rule 1: nothing to
 * press). So by the time Cancel is pressed, the "stored" version it would
 * revert to IS the edited version — the button read as a way out and left the
 * edit in place, every time (spec 5.1). Putting her words back is therefore a
 * real save of the snapshot taken when the editor opened, not a local reset.
 *
 * The snapshot carries STATUS as well as content, because the same editing
 * session can flip Draft/Done, and the first save marks a section Done on its
 * own. Cancel undoes the session, so it undoes both.
 *
 * Returns the record to save, or `null` when nothing changed and there is
 * nothing to write. Callers MUST treat null as "no write needed" rather than
 * as a failure.
 */
export function revertSection(section, snapshot) {
  if (!section || !snapshot) return null;
  const before = snapshot.content || {};
  const now = section.content || {};
  const sameContent = JSON.stringify(before) === JSON.stringify(now);
  const sameStatus = (snapshot.status || null) === (section.status || null);
  if (sameContent && sameStatus) return null;
  return { ...section, content: before, status: snapshot.status };
}

/* Where a page's drafting chat starts, and how many words are on it — both are
   questions about a list of blocks rather than about a website, so they live in
   sectionStore.js beside the store and the funnel pages use them too.
   Re-exported here, which is where the Website panel already looks. */
export { pageWords, startSection } from "./sectionStore";

export function useWebsiteSections() {
  const store = useSectionRecords();
  const { sectionsFor, save, remove, newId, loading, error } = store;

  /* Writing the blocks of one page. `from` is the locked text they were built
     from — stored whole on every row so a later "has this changed?" needs no
     reassembly, and so she can still see the original behind an edited block. */
  const writeBlocks = useCallback(async (page, blocks, from) => {
    const hash = from ? hashSource(from) : null;
    const made = [];
    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i];
      const saved = await save({
        id: newId(),
        pageId: page.id,
        sectionType: b.kind || OTHER_KIND,
        sortOrder: i,
        content: { heading: b.heading || "", text: b.text || "" },
        status: "draft",
        source: WEBSITE_SOURCE,
        sourceSection: b.sourceSection || null,
        sourceText: from || null,
        sourceHash: hash,
      });
      if (saved) made.push(saved);
    }
    return made;
  }, [save, newId]);

  /* The one-time split, run when a page is opened.
     Guards, each load-bearing:
       - `loading` / `error` — an empty list that hasn't arrived is NOT an empty
         page. Splitting against it would double every block. The caller re-runs
         this once loading settles.
       - existing sections — the split only ever happens on an empty page.
       - `attempted` — deleting every block and reopening the page must not
         resurrect them. (Same accepted gap as the page seed: delete every block
         AND reload the app and the split runs again.) */
  const attempted = useRef(new Set());
  const ensureSections = useCallback(async (page, outputs = {}) => {
    if (!page?.id || loading || error) return [];
    if (attempted.current.has(page.id)) return [];
    if (sectionsFor(page.id).length) return [];
    const blocks = sectionBlocks(page, outputs);
    if (!blocks.length) return [];
    attempted.current.add(page.id);
    return writeBlocks(page, blocks, pageSourceText(page, outputs));
  }, [loading, error, sectionsFor, writeBlocks]);

  /* The opt-in re-split after she re-locks My Website. Destructive on purpose:
     it replaces the blocks that came from the Accelerator and keeps the ones she
     wrote herself, which is why the caller must confirm first. */
  const refreshFromSource = useCallback(async (page, outputs = {}) => {
    const blocks = sectionBlocks(page, outputs);
    if (!page?.id || !blocks.length) return [];
    const existing = sectionsFor(page.id);
    for (const s of existing) {
      if (isFromMyWebsite(s)) await remove(s.id);
    }
    attempted.current.add(page.id);
    const made = await writeBlocks(page, blocks, pageSourceText(page, outputs));
    /* Her own blocks move below the replacements rather than keeping numbers
       that now collide with them — two blocks sharing a sort_order put the page
       in an order nothing on screen explains. */
    const kept = existing.filter((s) => !isFromMyWebsite(s));
    for (let i = 0; i < kept.length; i++) {
      await save({ ...kept[i], sortOrder: made.length + i });
    }
    return made;
  }, [sectionsFor, remove, save, writeBlocks]);

  return { ...store, ensureSections, refreshFromSource };
}
